import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Business as BusinessIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  SettingsBrightness as SettingsBrightnessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useLanguage } from '../contexts/LanguageContext';
import DialogueConfirmation from '../components/commun/DialogueConfirmation';
import { useThemeMode } from '../contexts/ThemeModeContext';
import EnteteEcran from '../components/commun/EnteteEcran';
import { apiService, fileUrl } from '../services/api';

// D-UI-4 : sans client ID, pas de bouton de liaison (la déliaison, elle, reste possible
// puisqu'elle n'appelle jamais Google).
const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const Profile = ({ showSnackbar }) => {
  const { t, language } = useLanguage();
  // ⚠️ Le mode vient du CONTEXTE, pas du profil serveur : il n'a aucune raison
  // de transiter par une requête, et le faire créerait un clignotement au
  // chargement (cf. `ThemeModeContext`).
  const { mode, modeEffectif, changerMode } = useThemeMode();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  const [aConfirmer, setAConfirmer] = useState(null);
  const [confirmationEnCours, setConfirmationEnCours] = useState(false);

  const executerConfirmation = async () => {
    if (!aConfirmer?.executer) return;
    setConfirmationEnCours(true);
    try {
      await aConfirmer.executer();
    } finally {
      setConfirmationEnCours(false);
      setAConfirmer(null);
    }
  };

  const [user, setUser] = useState(null);
  /**
   * Raison sociale AFFICHÉE, en lecture seule (lot 6e-iv).
   *
   * ⚠️ DÉLIBÉRÉMENT HORS DE `profileForm`. Ce state porte `Company.name`, une
   * donnée qui ne s'écrit PAS depuis cet écran : elle vient de la clé `company`
   * de `GET /auth/profile` et se modifie dans l'onglet « Ma société ».
   * `profileForm`, lui, est ce que le formulaire RENVOIE au serveur — y mêler
   * une valeur en lecture seule brouillerait la seule chose qui compte ici :
   * ce qui part dans le payload.
   * (Historique : tant que le payload portait encore `company_name`, les lier
   * aurait RECOPIÉ `Company.name` dans `User.company_name` à chaque
   * enregistrement. La clé a disparu du payload au lot 6d-iv, mais la
   * séparation reste la bonne forme.)
   */
  const [raisonSociale, setRaisonSociale] = useState(null);
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    // Pas de clé `address` — champ retiré (lot 6c) — ni `company_name` ni
    // `phone` — champs retirés (lot 6d-iv). Le payload est construit
    // explicitement plus bas : une clé absente n'est pas envoyée, et les
    // routes testent `if '<clé>' in data`, donc les colonnes existantes sont
    // PRÉSERVÉES au lieu d'être écrasées par une chaîne vide.
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [avatarPreview, setAvatarPreview] = useState(null);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const response = await apiService.get('/auth/profile');
        // Contrat mesuré : { user: {...}, stocks: [...] }. Pas de repli deviné —
        // `response.data || response` puis `data.user || data` pouvaient produire un
        // objet qui n'est PAS un utilisateur, et le poser dans le state.
        const u = response.data?.user;
        if (!u || typeof u !== 'object') {
          console.error('GET /auth/profile : forme inattendue', response.data);
          return;
        }
        setUser(u);
        setProfileForm({
          first_name: u.first_name || '',
          last_name: u.last_name || '',
          email: u.email || '',
        });
        if (u.avatar_path) {
          setAvatarPreview(fileUrl(u.avatar_path));
        }

        // ── La raison sociale arrive avec le profil, pour TOUS les rôles ────
        // `GET /auth/profile` rend une clé `company` À CÔTÉ de `user` (lot
        // 6e-iv) : `{ name }` seul, ou `null` si l'utilisateur n'a pas
        // d'entreprise (le SUPER_ADMIN). Aucun appel supplémentaire, aucune
        // branche sur le rôle, aucun 403 à avaler — le USER lit exactement la
        // même valeur que l'ADMIN, et le cas « champ vide » disparaît par
        // construction plutôt que par un correctif.
        setRaisonSociale(response.data?.company?.name ?? null);
      } catch (error) {
        console.error('Erreur chargement profil:', error);
        showSnackbar?.(t('settings.loadError'), 'error');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [showSnackbar, t]);

  /**
   * ⚠️ `role === 'ADMIN'` STRICTEMENT, et non `isPrivilegedRole` : la question
   * posée ici n'est pas « ce rôle est-il privilégié ? » mais « ce lien mène-t-il
   * quelque part ? ». Or `Sidebar.js:276` et `Admin.js:131` comparent eux aussi
   * la chaîne en dur, ce qui EXCLUT le SUPER_ADMIN d'`/admin`. Un lien affiché
   * pour lui le renverrait vers un écran qui le redirige. Cet écart entre le
   * front et le backend est noté 🟡 au ROADMAP ; le jour où il sera tranché,
   * cette condition doit suivre — d'où ce commentaire plutôt qu'un
   * `isPrivilegedRole` qui aurait l'air correct et ne le serait pas.
   */
  const estAdmin = user?.role === 'ADMIN';

  const handleProfileChange = (field) => (e) => {
    setProfileForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handlePasswordChange = (field) => (e) => {
    setPasswordForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      const payload = {
        first_name: profileForm.first_name,
        last_name: profileForm.last_name,
        // ⚠️ Ni `address` (lot 6c), ni `company_name`, ni `phone` (lot 6d-iv).
        // `update_profile` teste `if '<clé>' in data` : leur absence PRÉSERVE
        // la valeur en base au lieu de l'écraser par une chaîne vide. Le
        // profil ne porte plus que l'identité de la PERSONNE ; la raison
        // sociale vient de la société, le téléphone du stock.
      };
      const response = await apiService.put('/auth/profile', payload);
      // Même contrat que le GET. Le repli `|| response.data || payload` masquait un
      // changement de forme en réinjectant ce qu'on venait d'envoyer.
      const updatedUser = response.data?.user;
      if (!updatedUser || typeof updatedUser !== 'object') {
        console.error('PUT /auth/profile : forme inattendue, state non modifié', response.data);
        showSnackbar(t('messages.operationError'), 'error');
        return;
      }
      setUser((prev) => ({ ...(prev || {}), ...updatedUser }));
      // Mettre à jour le localStorage pour refléter le nouveau nom
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const u = JSON.parse(storedUser);
        localStorage.setItem('user', JSON.stringify({ ...u, ...updatedUser }));
      }
      showSnackbar?.(t('settings.nameUpdated'), 'success');
      // Recharger pour que le header récupère le nouvel avatar
      window.location.reload();
    } catch (error) {
      console.error('Erreur maj profil:', error);
      showSnackbar?.(t('settings.updateError'), 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSavePassword = async () => {
    const { current_password, new_password, confirm_password } = passwordForm;
    if (!current_password || !new_password || !confirm_password) {
      showSnackbar?.(t('settings.passwordAllRequired'), 'warning');
      return;
    }
    if (new_password !== confirm_password) {
      showSnackbar?.(t('settings.passwordMismatch'), 'warning');
      return;
    }
    try {
      setSavingPassword(true);
      await apiService.post('/auth/change-password', {
        current_password,
        new_password,
      });
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
      showSnackbar?.(t('settings.passwordUpdated'), 'success');
    } catch (error) {
      console.error('Erreur mot de passe:', error);
      const msg =
        error.response?.data?.error || error.message || t('settings.updateError');
      showSnackbar?.(msg, 'error');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setUploadingAvatar(true);
      const formData = new FormData();
      formData.append('avatar', file);
      // Utiliser le helper upload qui gère correctement multipart/form-data
      const response = await apiService.upload('/auth/profile/avatar', formData);
      const updatedUser = response.data?.user;
      if (!updatedUser || typeof updatedUser !== 'object') {
        console.error('POST /auth/profile/avatar : forme inattendue, state non modifié', response.data);
        showSnackbar(t('messages.operationError'), 'error');
        return;
      }
      setUser(updatedUser);
      setAvatarPreview(
        updatedUser.avatar_path
          ? fileUrl(updatedUser.avatar_path)
          : null
      );
      // Mettre à jour le localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const u = JSON.parse(storedUser);
        localStorage.setItem('user', JSON.stringify({ ...u, ...updatedUser }));
      }
      showSnackbar?.(t('settings.nameUpdated'), 'success');
    } catch (error) {
      console.error('Erreur avatar:', error);
      showSnackbar?.(t('settings.updateError'), 'error');
    } finally {
      setUploadingAvatar(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <Typography>Chargement...</Typography>
      </Box>
    );
  }

  if (!user) {
    return null;
  }

  // Après une mutation Google : relire le profil serveur pour que google_linked /
  // google_email ne restent pas périmés, et resynchroniser localStorage['user'] (lu au
  // démarrage de l'app). On NE ré-hydrate PAS profileForm : cela écraserait des saisies
  // non enregistrées du formulaire de profil.
  // Forme du contrat serveur, mesurée : GET /auth/profile -> { user: {...}, stocks: [...] }.
  // Pas de fallback : si `user` manque, on n'écrit RIEN. Un affichage périmé vaut mieux
  // qu'un localStorage['user'] corrompu, dont l'app repartirait au prochain démarrage.
  const refreshUser = async () => {
    const response = await apiService.get('/auth/profile');
    const u = response.data?.user;
    if (!u || typeof u !== 'object') {
      console.error('GET /auth/profile : forme inattendue, localStorage non modifié', response.data);
      return;
    }
    setUser(u);
    localStorage.setItem('user', JSON.stringify(u));
  };

  const handleGoogleLinkSuccess = async ({ credential }) => {
    setGoogleBusy(true);
    try {
      // Seul le POST détermine le succès de l'opération.
      try {
        await apiService.post('/auth/google/link', { credential });
      } catch (error) {
        console.error('Erreur liaison Google:', error);
        // 409 métier relayés bruts : « déjà lié à un autre utilisateur », « déliez-le d'abord ».
        showSnackbar?.(error.response?.data?.error || t('settings.googleLinkError'), 'error');
        return;
      }
      // Ici la liaison EST faite côté serveur. Le refetch n'est qu'un rafraîchissement
      // d'affichage : son échec ne doit JAMAIS transformer ce succès en erreur.
      try {
        await refreshUser();
      } catch (error) {
        console.error('Rafraîchissement du profil après liaison échoué:', error);
      }
      showSnackbar?.(t('settings.googleLinkSuccess'), 'success');
    } finally {
      setGoogleBusy(false);
    }
  };

  // Échec côté Google (popup fermée, script indisponible) : aucune réponse HTTP à relayer.
  const handleGoogleLinkError = () => {
    showSnackbar?.(t('settings.googleLinkError'), 'error');
  };

  /**
   * 🔴 CETTE ANNONCE-CI RASSURE AU LIEU D'ALARMER, ET C'EST DÉLIBÉRÉ.
   *
   * Délier Google est **réversible** : le compte reste accessible par mot de
   * passe, et la liaison peut être refaite. Le serveur porte même une garde
   * anti-lock-out — il refuse en 409 si aucun mot de passe n'est défini.
   *
   * ⚠️ *Une annonce alarmante sur un geste inoffensif fait renoncer à un geste
   * inoffensif* — et elle apprend à ignorer les annonces suivantes, qui elles
   * porteront de vrais avertissements. **La couleur du bouton suit : `primary`,
   * pas `error`.**
   */
  const demanderDeliaisonGoogle = () => setAConfirmer({
    executer: handleGoogleUnlink,
  });

  const handleGoogleUnlink = async () => {
    setGoogleBusy(true);
    try {
      // Seul le POST détermine le succès de l'opération.
      try {
        await apiService.post('/auth/google/unlink');
      } catch (error) {
        console.error('Erreur déliaison Google:', error);
        // 409 métier : « aucun mot de passe défini » (garde anti lock-out).
        showSnackbar?.(error.response?.data?.error || t('settings.googleUnlinkError'), 'error');
        return;
      }
      // Ici la déliaison EST faite côté serveur : le refetch ne peut pas la démentir.
      try {
        await refreshUser();
      } catch (error) {
        console.error('Rafraîchissement du profil après déliaison échoué:', error);
      }
      showSnackbar?.(t('settings.googleUnlinkSuccess'), 'success');
    } finally {
      setGoogleBusy(false);
    }
  };

  return (
    <Box>
      {/* 🔴 `EnteteEcran`, COMME LES SIX AUTRES ÉCRANS DE TRAVAIL. Le Profil
          portait encore un `<Typography variant="h4">` nu — la correction du lot
          RU2 ne l'avait pas atteint. Mesuré : six écrans sur neuf l'emploient,
          et les trois qui manquent sont Profil, Notifications et Admin.
          🔴 ET LE TITRE DISAIT « PARAMÈTRES » PENDANT QUE LE MENU DIT
          « MON PROFIL ». On cliquait un mot et on en lisait un autre — la même
          famille que le vocabulaire de règlement du lot RU4-a. *L'URL et le
          menu disent tous deux « profil » ; c'était le titre qui était seul de
          son avis.* Le libellé du menu l'emporte : c'est celui que
          l'utilisateur a lu avant de cliquer. */}
      <EnteteEcran titre={t('common.profile')} />
      <Typography variant="body2" color="text.secondary" sx={{ mt: -2, mb: 3 }}>
        {t('settings.subtitle')}
      </Typography>

      {/* 🔴 DEUX COLONNES, ET L'IDENTITÉ EST SEULE DANS LA SIENNE. Les quatre
          cartes étaient en `md={6}`, donc une grille 2 × 2 : la ligne du haut
          appariait la carte la PLUS HAUTE (identité — avatar, quatre champs, un
          bouton) avec la plus COURTE (apparence — trois boutons). MUI aligne
          les rangées sur la plus haute : la carte apparence flottait au-dessus
          d'un vide de sa hauteur.
          ⚠️ *Une grille régulière n'est pas une mise en page : elle suppose que
          tout ce qu'on y range a la même taille.* Les trois blocs d'ACTION —
          apparence, mot de passe, Google — s'empilent maintenant en face de
          l'identité, qui est le seul bloc de SAISIE. */}
      <Grid container spacing={3} alignItems="flex-start">
        {/* Profil utilisateur */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('settings.profileSection')}
              </Typography>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar
                  src={avatarPreview || undefined}
                  sx={{ width: 64, height: 64, fontSize: 24 }}
                >
                  {!avatarPreview &&
                    (user.first_name?.[0] || '') + (user.last_name?.[0] || '')}
                </Avatar>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={uploadingAvatar}
                >
                  {uploadingAvatar ? 'Upload...' : t('settings.changeAvatar')}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleAvatarChange}
                  />
                </Button>
              </Box>
              <TextField
                fullWidth
                margin="normal"
                label={t('admin.firstName')}
                value={profileForm.first_name}
                onChange={handleProfileChange('first_name')}
              />
              <TextField
                fullWidth
                margin="normal"
                label={t('admin.lastName')}
                value={profileForm.last_name}
                onChange={handleProfileChange('last_name')}
              />
              <TextField
                fullWidth
                margin="normal"
                label={t('admin.email')}
                type="email"
                value={profileForm.email}
                disabled
              />
              {/* ─── Raison sociale — LECTURE SEULE (lot 6e-iv) ───────────
                  Le profil est l'identité d'une PERSONNE. Une entreprise a UN
                  nom, qui ne doit pas pouvoir être réécrit depuis N profils :
                  c'était le cas jusqu'ici, et deux collègues pouvaient faire
                  diverger la raison sociale de leurs bons de commande sans
                  jamais se croiser.
                  ⚠️ `value` vient de `raisonSociale`, PAS de `profileForm` :
                  cet écran AFFICHE `Company.name`, il ne l'envoie jamais.
                  Cf. la déclaration du state.
                  ⚠️ LE CHAMP DISPARAÎT s'il n'y a pas d'entreprise (le
                  SUPER_ADMIN, `company_id` NULL). Un libellé au-dessus d'un
                  champ vide laisse croire que l'information MANQUE alors
                  qu'elle n'existe pas : c'est très exactement le défaut que ce
                  lot corrige, il serait absurde de le rejouer d'un cran. Pas
                  d'entreprise, pas de ligne.
                  ⚠️ CE CAS EST INVÉRIFIABLE AU NAVIGATEUR AUJOURD'HUI : le
                  SUPER_ADMIN n'a pas de page Profil, il est intercepté par le
                  châssis dédié d'UI-4 (sans Header ni Sidebar), donc
                  `/profile` ne lui est pas accessible. La garde est écrite
                  pour le jour où il en aura une — ne pas chercher à la tester
                  à l'écran, elle n'est atteignable que par le test. */}
              {raisonSociale && (
                <TextField
                  fullWidth
                  margin="normal"
                  label={t('admin.companyName')}
                  value={raisonSociale}
                  disabled
                  helperText={
                    estAdmin
                      ? t('company.editedInTab')
                      : t('company.managedByAdmin')
                  }
                />
              )}
              {/* Le renvoi n'existe QUE pour un ADMIN, et c'est mesuré, pas
                  supposé : `Sidebar.js` n'affiche l'entrée « Administration »
                  que si `role === 'ADMIN'`, et `Admin.js` redirige tout autre
                  rôle vers `/`. Proposer le lien à un USER l'enverrait sur un
                  écran qui le rejette — un cul-de-sac de plus, celui-là créé
                  par le correctif censé en fermer un. */}
              {estAdmin && raisonSociale && (
                <Box mt={0.5}>
                  <Button
                    size="small"
                    startIcon={<BusinessIcon />}
                    onClick={() => navigate('/admin', { state: { tab: 2 } })}
                  >
                    {t('company.goToTab')}
                  </Button>
                </Box>
              )}
              {/* ⚠️ TROIS CHAMPS RETIRÉS ICI, et ils ne doivent pas revenir :
                  « adresse » (lot 6c), « nom de l'entreprise » et « téléphone »
                  (lot 6d-iv). Le modèle métier arrêté dit qu'un UTILISATEUR
                  porte identité et accès, rien d'autre : la raison sociale
                  appartient à la SOCIÉTÉ (`Company.name`, onglet « Ma
                  société »), l'adresse et le téléphone au STOCK émetteur
                  (`Stock.address` / `Stock.phone`, dialogue de stock d'/admin).
                  ⚠️ Le retrait a attendu que les documents basculent : depuis
                  6d-i, `pdf_service` et `email_service` lisent `Company.name`
                  et `Stock.phone`. Retirer ces champs AVANT aurait vidé les
                  bons de commande. Mesuré avant de le faire — les 3 colonnes
                  User sont NULL sur 6/6 comptes, aucun document ne change.
                  ⚠️ Les colonnes restent EN BASE (cf. ROADMAP, colonnes
                  mortes) : un `DROP COLUMN` est une migration, pas un lot
                  d'interface. */}
              <Box mt={2} textAlign="right">
                <Button
                  variant="contained"
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                >
                  {t('common.save')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* ── LA COLONNE DES TROIS BLOCS D'ACTION ─────────────────────────
            Apparence, mot de passe, compte Google : trois gestes courts, en
            face du seul bloc de SAISIE. Ils s'empilent au lieu de partager
            une rangée avec l'identité, dont la hauteur les laissait flotter
            au-dessus d'un vide. */}
        <Grid item xs={12} md={6}>
          <Grid container spacing={3}>
          {/* ═══ APPARENCE ═══════════════════════════════════════════════
              🔴 CETTE CARTE N'ÉCRIT RIEN SUR LE SERVEUR, ET C'EST STRUCTURANT.
              Le mode d'affichage n'entre NI dans `profileForm`, NI dans aucune
              requête : il vit dans `localStorage`, via `ThemeModeContext`.

              ⚠️ DEUX NATURES, DEUX CHEMINS — la même règle que `raisonSociale`
              plus haut, pour une raison différente :
                · `raisonSociale` est hors de `profileForm` parce qu'elle
                  appartient à la SOCIÉTÉ : l'y mêler recopierait `Company.name`
                  dans `User.company_name` à chaque enregistrement ;
                · le mode est hors de `profileForm` parce qu'il appartient à
                  l'APPAREIL. La même personne peut vouloir du sombre sur son
                  portable le soir et du clair sur le poste de la clinique.
              Dans les deux cas, le bouton « Enregistrer » de la carte du dessus
              ne doit jamais emporter cette valeur.

              ⚠️ Et le mode s'applique IMMÉDIATEMENT, sans enregistrement : il n'y
              a rien à confirmer, donc pas de bouton. Un réglage d'affichage qui
              demanderait une validation ferait douter qu'il ait été pris. */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('settings.appearanceSection')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('settings.appearanceHint')}
                </Typography>
                <ToggleButtonGroup
                  value={mode}
                  exclusive
                  onChange={(_, valeur) => { if (valeur) changerMode(valeur); }}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="clair">
                    <LightModeIcon fontSize="small" sx={{ mr: 1 }} />
                    {t('settings.themeLight')}
                  </ToggleButton>
                  <ToggleButton value="sombre">
                    <DarkModeIcon fontSize="small" sx={{ mr: 1 }} />
                    {t('settings.themeDark')}
                  </ToggleButton>
                  <ToggleButton value="auto">
                    <SettingsBrightnessIcon fontSize="small" sx={{ mr: 1 }} />
                    {t('settings.themeAuto')}
                  </ToggleButton>
                </ToggleButtonGroup>
                {/* En mode `auto`, on DIT ce qui s'applique : sans cela,
                    l'utilisateur ne peut pas savoir si son choix a été pris ou si
                    le système l'a emporté. */}
                {mode === 'auto' && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {t('settings.themeAutoActive')} : {t(modeEffectif === 'sombre'
                      ? 'settings.themeDark' : 'settings.themeLight')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Mot de passe */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('settings.passwordSection')}
                </Typography>
                <TextField
                  fullWidth
                  margin="normal"
                  label={t('settings.currentPassword')}
                  type="password"
                  value={passwordForm.current_password}
                  onChange={handlePasswordChange('current_password')}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label={t('settings.newPassword')}
                  type="password"
                  value={passwordForm.new_password}
                  onChange={handlePasswordChange('new_password')}
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label={t('settings.confirmPassword')}
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={handlePasswordChange('confirm_password')}
                />
                <Box mt={2} textAlign="right">
                  <Button
                    variant="contained"
                    onClick={handleSavePassword}
                    disabled={savingPassword}
                  >
                    {t('settings.updatePasswordButton')}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Compte Google (6e-iv) */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('settings.googleSection')}
                </Typography>

                {user?.google_linked ? (
                  <>
                    {/* google_email est NULL si Google n'a pas marqué l'adresse vérifiée :
                        on affiche alors « Compte lié » seul, sans email. */}
                    <Typography variant="body2">
                      {t('settings.googleLinked')}
                      {user.google_email ? ` — ${user.google_email}` : ''}
                    </Typography>
                    <Box mt={2} textAlign="right">
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={demanderDeliaisonGoogle}
                        disabled={googleBusy}
                      >
                        {t('settings.googleUnlinkButton')}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {t('settings.googleNotLinkedYet')}
                    </Typography>
                    {googleClientId && (
                      <>
                        {/* Le libellé du bouton est rendu par Google : cette ligne est la
                            consigne, pas un label de bouton. */}
                        <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                          {t('settings.googleLinkButton')}
                        </Typography>
                        <GoogleOAuthProvider key={language} clientId={googleClientId} locale={language}>
                          <Box display="flex" justifyContent="center">
                            <GoogleLogin
                              onSuccess={handleGoogleLinkSuccess}
                              onError={handleGoogleLinkError}
                              text="continue_with"
                              shape="rectangular"
                              width="280"
                            />
                          </Box>
                        </GoogleOAuthProvider>
                      </>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
          </Grid>
        </Grid>

      {/* 🔴 LE `<Divider>` FINAL A ÉTÉ RETIRÉ : il était placé APRÈS la grille,
          donc il ne séparait RIEN — il traçait une ligne entre le dernier
          bloc et le bas de la page. *Un séparateur qui ne sépare rien n'est
          pas un détail de style : il fait chercher ce qui vient après.* */}

      {/* ⚠️ COULEUR `primary`, PAS `error` : le geste est réversible. *Une
          annonce alarmante sur un geste inoffensif apprend à ignorer les
          suivantes.* */}
      <DialogueConfirmation
        ouvert={Boolean(aConfirmer)}
        titre={t('settings.googleUnlinkTitle')}
        question={t('settings.googleUnlinkQuestion')}
        consequences={t('settings.googleUnlinkConsequence')}
        libelleConfirmer={t('settings.googleUnlink')}
        couleur="primary"
        enCours={confirmationEnCours}
        onConfirmer={executerConfirmation}
        onFermer={() => setAConfirmer(null)}
      />
    </Box>
  );
};

export default Profile;


